import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SkillGraphData, SkillGraphEdgeKind } from "../lib/graph-types";
import {
  clusterColor,
  DEFAULT_GRAPH_KINDS,
  layoutGraph,
  nodeRadius,
  type LayoutNode,
} from "../lib/graph-layout";
import {
  boundsFromNodes,
  fitViewport,
  focusWorldPoint,
  screenToWorld,
  zoomAtPoint,
  type Viewport,
} from "../lib/graph-viewport";
import { navigateToSkill } from "../router";

const WORLD_W = 2800;
const WORLD_H = 1900;

const KIND_LABEL: Record<SkillGraphEdgeKind, string> = {
  overlap: "Overlap",
  "gap-overlap": "Gap",
  mentions: "Mentions",
  category: "Category",
  router: "Router",
  research: "Research",
  autoresearch: "Autoresearch",
  "co-tier": "Tier",
};

const KIND_EDGE: Record<SkillGraphEdgeKind, string> = {
  overlap: "#0a0a0a",
  "gap-overlap": "#c45d00",
  mentions: "#4a6fa5",
  category: "#8a8580",
  router: "#1b4d3e",
  research: "#6b4c9a",
  autoresearch: "#9c2f2f",
  "co-tier": "#8a8580",
};

interface Props {
  graph: SkillGraphData;
  selected: string | null;
  onSelect: (id: string | null) => void;
}

function drawLabelPill(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  scale: number,
) {
  const fontPx = Math.min(14, Math.max(9, 11 / scale));
  ctx.font = `600 ${fontPx}px Inter, system-ui, sans-serif`;
  const w = ctx.measureText(text).width + fontPx * 1.2;
  const h = fontPx * 1.45;
  const rx = x - w / 2;
  const ry = y - h / 2;
  ctx.fillStyle = "rgba(245, 242, 236, 0.96)";
  ctx.strokeStyle = "rgba(10, 10, 10, 0.85)";
  ctx.lineWidth = 1.5 / scale;
  ctx.beginPath();
  ctx.roundRect(rx, ry, w, h, 2 / scale);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = "#0a0a0a";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, x, y + fontPx * 0.05);
}

export function KnowledgeGraphExplorer({ graph, selected, onSelect }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const nodesRef = useRef<LayoutNode[]>([]);
  const viewRef = useRef<Viewport>({ scale: 0.4, tx: 0, ty: 0 });
  const panRef = useRef<{ x: number; y: number } | null>(null);
  const [hoverSkill, setHoverSkill] = useState<{ id: string; x: number; y: number } | null>(null);
  const dragNodeRef = useRef<string | null>(null);
  const hoverRef = useRef<string | null>(null);
  const frameRef = useRef(0);
  const [viewTick, setViewTick] = useState(0);
  const bumpView = () => setViewTick((t) => t + 1);

  const [kindFilter, setKindFilter] = useState<Set<SkillGraphEdgeKind>>(
    () => new Set(DEFAULT_GRAPH_KINDS),
  );
  const [search, setSearch] = useState("");
  const [showAllLabels, setShowAllLabels] = useState(true);

  const filteredEdges = useMemo(
    () => graph.edges.filter((e) => kindFilter.has(e.kind)),
    [graph.edges, kindFilter],
  );

  const layoutNodes = useMemo(
    () => layoutGraph(graph, filteredEdges, WORLD_W, WORLD_H),
    [graph, filteredEdges],
  );

  useEffect(() => {
    nodesRef.current = layoutNodes.map((n) => ({ ...n }));
  }, [layoutNodes]);

  const searchMatch = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    return new Set(graph.nodes.filter((n) => n.id.includes(q)).map((n) => n.id));
  }, [graph.nodes, search]);

  const fitView = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || nodesRef.current.length === 0) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width < 10 || rect.height < 10) return;
    const bounds = boundsFromNodes(nodesRef.current, 120);
    if (!bounds) return;
    viewRef.current = fitViewport(bounds, rect.width, rect.height, 1.1);
    bumpView();
  }, []);

  const applyZoom = useCallback((factor: number, centerClient?: { x: number; y: number }) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const cx = centerClient?.x ?? rect.left + rect.width / 2;
    const cy = centerClient?.y ?? rect.top + rect.height / 2;
    viewRef.current = zoomAtPoint(viewRef.current, rect, cx, cy, factor);
    bumpView();
  }, []);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(() => fitView());
    });
    ro.observe(wrap);
    requestAnimationFrame(() => fitView());
    return () => ro.disconnect();
  }, [fitView, layoutNodes]);

  const pickNode = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const { x, y } = screenToWorld(viewRef.current, rect, clientX, clientY);
    const scale = viewRef.current.scale;
    let best: LayoutNode | null = null;
    let bestD = Infinity;
    for (const n of nodesRef.current) {
      const r = nodeRadius(n, scale);
      const d = Math.hypot(n.x - x, n.y - y);
      if (d <= r + 6 / scale && d < bestD) {
        bestD = d;
        best = n;
      }
    }
    return best;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let running = true;

    const draw = () => {
      if (!running || !ctx) return;
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      if (w < 2 || h < 2) {
        frameRef.current = requestAnimationFrame(draw);
        return;
      }

      const targetW = Math.round(w * dpr);
      const targetH = Math.round(h * dpr);
      if (canvas.width !== targetW || canvas.height !== targetH) {
        canvas.width = targetW;
        canvas.height = targetH;
      }

      const view = viewRef.current;
      const scale = view.scale;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = "#f5f2ec";
      ctx.fillRect(0, 0, w, h);

      ctx.save();
      ctx.translate(view.tx, view.ty);
      ctx.scale(scale, scale);

      const nodeMap = new Map(nodesRef.current.map((n) => [n.id, n]));
      const strokePx = (px: number) => px / scale;

      for (const [cid, ids] of Object.entries(graph.neuralCommunities || {})) {
        let sx = 0;
        let sy = 0;
        let c = 0;
        for (const id of ids) {
          const n = nodeMap.get(id);
          if (!n) continue;
          sx += n.x;
          sy += n.y;
          c++;
        }
        if (c === 0) continue;
        const color = clusterColor(cid);
        const cr = 70 + Math.sqrt(c) * 35;
        ctx.beginPath();
        ctx.arc(sx / c, sy / c, cr, 0, Math.PI * 2);
        ctx.fillStyle = color + "28";
        ctx.fill();
        ctx.strokeStyle = color;
        ctx.lineWidth = strokePx(2.5);
        ctx.globalAlpha = 0.55;
        ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.font = `700 ${strokePx(11)}px JetBrains Mono, monospace`;
        ctx.fillStyle = color;
        ctx.textAlign = "center";
        ctx.fillText(cid.replace("cluster-", "C"), sx / c, sy / c - cr - strokePx(8));
      }

      for (const e of filteredEdges) {
        const a = nodeMap.get(e.source);
        const b = nodeMap.get(e.target);
        if (!a || !b) continue;
        const highlighted =
          selected === a.id ||
          selected === b.id ||
          hoverRef.current === a.id ||
          hoverRef.current === b.id;
        const mx = (a.x + b.x) / 2;
        const my = (a.y + b.y) / 2;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.hypot(dx, dy) || 1;
        const curve = dist * 0.12;
        const nx = -dy / dist;
        const ny = dx / dist;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.quadraticCurveTo(mx + nx * curve, my + ny * curve, b.x, b.y);
        ctx.strokeStyle = KIND_EDGE[e.kind] ?? "#0a0a0a";
        ctx.globalAlpha = highlighted ? 0.85 : 0.42;
        ctx.lineWidth = strokePx(highlighted ? 2.8 : 1.8);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }

      const labelIds = new Set<string>();
      if (selected) labelIds.add(selected);
      if (hoverRef.current) labelIds.add(hoverRef.current);
      if (searchMatch) {
        for (const id of searchMatch) labelIds.add(id);
      }
      for (const n of nodesRef.current) {
        if (showAllLabels && (n.hubScore >= 22 || n.degree >= 8)) labelIds.add(n.id);
      }

      for (const n of nodesRef.current) {
        const r = nodeRadius(n, scale);
        const isSel = selected === n.id;
        const isHover = hoverRef.current === n.id;
        const isMatch = searchMatch?.has(n.id);
        const dim = searchMatch && !isMatch && !isSel && !isHover;
        const fill = clusterColor(n.communityId);

        if (isSel || isHover || isMatch) {
          ctx.beginPath();
          ctx.arc(n.x, n.y, r + strokePx(10), 0, Math.PI * 2);
          ctx.fillStyle = isSel ? "rgba(255, 68, 56, 0.25)" : fill + "44";
          ctx.fill();
        }

        ctx.beginPath();
        ctx.arc(n.x, n.y, isSel ? r + strokePx(2) : r, 0, Math.PI * 2);
        ctx.fillStyle = dim ? "#b8b3aa" : fill;
        ctx.globalAlpha = dim ? 0.4 : 1;
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.strokeStyle = isSel ? "#ff4438" : "#0a0a0a";
        ctx.lineWidth = strokePx(isSel ? 3.5 : 2.5);
        ctx.stroke();

        if (labelIds.has(n.id)) {
          const short =
            n.id.length > 22 && scale < 0.5 ? n.id.replace(/^agent-/, "a-").slice(0, 20) : n.id;
          drawLabelPill(ctx, short, n.x, n.y - r - strokePx(14), scale);
        }
      }

      ctx.restore();

      const zoomPct = Math.round(scale * 100);
      ctx.font = "600 11px JetBrains Mono, monospace";
      ctx.fillStyle = "#0a0a0a";
      ctx.textAlign = "left";
      ctx.fillText(
        `${nodesRef.current.length} skills · ${filteredEdges.length} links · zoom ${zoomPct}%`,
        14,
        h - 14,
      );

      frameRef.current = requestAnimationFrame(draw);
    };

    frameRef.current = requestAnimationFrame(draw);
    return () => {
      running = false;
      cancelAnimationFrame(frameRef.current);
    };
  }, [filteredEdges, graph.neuralCommunities, selected, searchMatch, showAllLabels, viewTick]);

  const toggleKind = (k: SkillGraphEdgeKind) => {
    setKindFilter((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const focusNode = (id: string) => {
    const n = nodesRef.current.find((x) => x.id === id);
    const canvas = canvasRef.current;
    if (!n || !canvas) return;
    const rect = canvas.getBoundingClientRect();
    viewRef.current = focusWorldPoint(viewRef.current, rect, n.x, n.y, 1.15);
    bumpView();
    onSelect(id);
  };

  const zoomPct = Math.round(viewRef.current.scale * 100);

  return (
    <div
      ref={wrapRef}
      data-testid="graph-explorer"
      className="relative flex flex-col flex-1 min-h-[calc(100vh-140px)] border-t border-fg bg-bg"
    >
      <div className="absolute top-3 left-3 z-10 flex flex-wrap gap-2 items-start pointer-events-none max-w-[calc(100%-24px)]">
        <div className="pointer-events-auto flex flex-wrap gap-2 items-center bg-bg border-2 border-fg px-3 py-2 shadow-[4px_4px_0_0_var(--fg)]">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Zoek skill…"
            className="font-mono text-[12px] w-44 sm:w-56 border-2 border-fg bg-bg px-2 py-1.5"
          />
          <div className="flex border-2 border-fg">
            <button
              type="button"
              onClick={() => applyZoom(1 / 1.25)}
              className="font-mono text-[14px] px-2 py-1 hover:bg-fg hover:text-bg"
              aria-label="Zoom out"
            >
              −
            </button>
            <span className="font-mono text-[10px] px-2 py-1 border-x-2 border-fg min-w-[52px] text-center">
              {zoomPct}%
            </span>
            <button
              type="button"
              onClick={() => applyZoom(1.25)}
              className="font-mono text-[14px] px-2 py-1 hover:bg-fg hover:text-bg"
              aria-label="Zoom in"
            >
              +
            </button>
          </div>
          <button
            type="button"
            onClick={() => fitView()}
            className="font-mono text-[10px] uppercase px-2 py-1.5 border-2 border-fg hover:bg-fg hover:text-bg"
          >
            Fit
          </button>
          <button
            type="button"
            onClick={() => setShowAllLabels((v) => !v)}
            className={`font-mono text-[10px] uppercase px-2 py-1.5 border-2 border-fg ${showAllLabels ? "bg-fg text-bg" : ""}`}
          >
            Hub labels
          </button>
        </div>
        <div className="pointer-events-auto flex flex-wrap gap-1 bg-bg border-2 border-fg px-2 py-2 shadow-[4px_4px_0_0_var(--fg)]">
          {(Object.keys(KIND_LABEL) as SkillGraphEdgeKind[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => toggleKind(k)}
              className={`font-mono text-[9px] uppercase px-2 py-1 border-2 ${
                kindFilter.has(k) ? "bg-fg text-bg border-fg" : "border-rule text-muted"
              }`}
              style={kindFilter.has(k) ? undefined : { borderColor: KIND_EDGE[k] }}
            >
              {KIND_LABEL[k]}
            </button>
          ))}
        </div>
      </div>

      <canvas
        ref={canvasRef}
        className="flex-1 w-full min-h-[560px] cursor-grab active:cursor-grabbing touch-none"
        aria-label="Interactive skill knowledge graph"
        onWheel={(e) => {
          e.preventDefault();
          const factor = Math.exp(-e.deltaY * 0.002);
          applyZoom(factor, { x: e.clientX, y: e.clientY });
        }}
        onPointerDown={(e) => {
          const node = pickNode(e.clientX, e.clientY);
          if (node) {
            dragNodeRef.current = node.id;
            (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
            return;
          }
          panRef.current = { x: e.clientX, y: e.clientY };
          (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          if (dragNodeRef.current) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const { x, y } = screenToWorld(viewRef.current, rect, e.clientX, e.clientY);
            const n = nodesRef.current.find((nn) => nn.id === dragNodeRef.current);
            if (n) {
              n.x = x;
              n.y = y;
            }
            return;
          }
          if (panRef.current) {
            viewRef.current = {
              ...viewRef.current,
              tx: viewRef.current.tx + (e.clientX - panRef.current.x),
              ty: viewRef.current.ty + (e.clientY - panRef.current.y),
            };
            panRef.current = { x: e.clientX, y: e.clientY };
            bumpView();
          }
          hoverRef.current = pickNode(e.clientX, e.clientY)?.id ?? null;
          if (hoverRef.current) {
            const node = pickNode(e.clientX, e.clientY);
            if (node) setHoverSkill({ id: node.id, x: e.clientX, y: e.clientY });
            else setHoverSkill(null);
          } else {
            setHoverSkill(null);
          }
        }}
        onPointerUp={(e) => {
          dragNodeRef.current = null;
          panRef.current = null;
          (e.target as HTMLCanvasElement).releasePointerCapture(e.pointerId);
        }}
        onPointerLeave={() => {
          hoverRef.current = null;
          setHoverSkill(null);
        }}
        onClick={(e) => {
          const node = pickNode(e.clientX, e.clientY);
          if (node) navigateToSkill(node.id);
        }}
        onDoubleClick={(e) => {
          const node = pickNode(e.clientX, e.clientY);
          if (node) focusNode(node.id);
          else fitView();
        }}
      />

      {hoverSkill && (
        <div
          className="absolute z-20 pointer-events-none border-2 border-fg bg-fg text-bg font-mono text-[11px] px-3 py-2 shadow-[4px_4px_0_0_var(--fg)]"
          style={{ left: hoverSkill.x + 16, top: hoverSkill.y - 12 }}
        >
          {hoverSkill.id}
        </div>
      )}
      {/* Selected skill info bar at bottom */}
      {selected && (
        <div className="absolute bottom-3 left-3 right-3 z-10 pointer-events-none">
          <div className="pointer-events-auto mx-auto max-w-fit flex items-center gap-4 bg-bg border-2 border-fg px-4 py-2 shadow-[4px_4px_0_0_var(--fg)] font-mono text-[11px]">
            {(() => {
              const node = graph.nodes.find((n) => n.id === selected);
              if (!node) return <span>{selected}</span>;
              return (
                <>
                  <span className="font-bold uppercase">{selected}</span>
                  <span className="text-muted">{node.communityId}</span>
                  <span>score {Math.round(node.combinedPct ?? 0)}%</span>
                  <span>hub {node.hubScore}</span>
                  <button
                    type="button"
                    onClick={() => navigateToSkill(selected)}
                    className="underline underline-offset-4 hover:text-accent font-bold"
                  >
                    View skill →
                  </button>
                  <button
                    type="button"
                    onClick={() => onSelect(null)}
                    className="text-muted hover:text-fg"
                  >
                    ×
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}